import React, { useEffect, useState } from 'react';
import {
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Input,
    Select,
    Grid,
    GridItem,
    useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { CheckIcon, EditIcon, CloseIcon } from '@chakra-ui/icons';
import { AdvancedDataTable } from './AdvancedDataTable';
import { createColumnHelper } from "@tanstack/react-table";

const AssetDetailsModal = ({ asset, isOpen, onClose }) => {
    const [transactions, setTransactions] = useState([]);
    const [newTransaction, setNewTransaction] = useState({
        date: new Date().toISOString().split('T')[0],   // set the default date to today in the format yyyy-mm-dd
        transaction_type: 'BUY',
        quantity: '1',
        price_per_unit: '1',
        net_total: '',
        fees: '0.0',
        gross_total: '',
    });
    const [assetPrice, setAssetPrice] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [editedAsset, setEditedAsset] = useState({ ...asset });
    const toast = useToast();

    const fetchTransactions = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`/api/assets/${asset.id}/transactions`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            let raw_transactions = response.data;

            // Calculate net and gross total for each transaction
            for (let i = 0; i < raw_transactions.length; i++) {
                raw_transactions[i].net_total = raw_transactions[i].price_per_unit * raw_transactions[i].quantity;
                raw_transactions[i].gross_total = raw_transactions[i].net_total + raw_transactions[i].fees;
            }

            setTransactions(raw_transactions);
        } catch (error) {
            console.error('Failed to fetch transactions', error);
        }
    };

    const fetchAssetPrice = async () => {
        const token = localStorage.getItem('token');

        if (asset.isin !== null) {
            try {
                const response = await axios.get(`/api/market_data/${asset.isin}/price`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                setAssetPrice(response.data.price);
            } catch (error) {
                console.error('Failed to fetch asset price', error);
            }
        }
    };

    const getAssetDetails = async () => {
        const token = localStorage.getItem('token');

        try {
            const response = await axios.get(`/api/assets/${asset.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            asset.quantity = response.data.quantity;
        } catch (error) {
            console.error('Failed to get asset details', error);
        }
    }

    useEffect(() => {
        if (asset) {
            fetchTransactions();
            fetchAssetPrice();
        }
    }, [asset]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setNewTransaction((prev) => ({ ...prev, [name]: value }));
    };

    const handleAssetInputChange = (e) => {
        const { name, value } = e.target;
        setEditedAsset((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditClick = () => {
        setEditedAsset({ ...asset });
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        setEditedAsset({ ...asset });
        setIsEditing(false);
    };

    const handleConfirmClick = async () => {
        const token = localStorage.getItem('token');
        try {
            await axios.post(`/api/assets/${asset.id}`, editedAsset, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast({
                title: 'Asset updated.',
                description: 'The asset details have been updated successfully.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });

            setIsEditing(false);
        } catch (error) {
            toast({
                title: 'Error updating asset.',
                description: 'There was an error updating the asset details.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const columnHelper = createColumnHelper();

    const columns = [
        columnHelper.accessor("date", {
          cell: (info) => info.getValue(),
          header: "Date",
        meta: {
            isDate: true
        }
        }),
        columnHelper.accessor("transaction_type", {
          cell: (info) => info.getValue(),
          header: "Type"
        }),
        columnHelper.accessor("quantity", {
          cell: (info) => info.getValue(),
          header: "Quantity",
          meta: {
            isNumeric: true
          }
        }),
        columnHelper.accessor("price_per_unit", {
            cell: (info) => info.getValue(),
            header: "Price per unit",
            meta: {
              isNumeric: true
            }
        }),
        columnHelper.accessor("net_total", {
            cell: (info) => parseFloat(info.getValue()).toFixed(2),
            header: "Net Total",
            meta: {
              isNumeric: true
            }
        }),
        columnHelper.accessor("fees", {
            cell: (info) => info.getValue(),
            header: "Fees",
            meta: {
              isNumeric: true
            }
        }),
        columnHelper.accessor("gross_total", {
            cell: (info) => parseFloat(info.getValue()).toFixed(2),
            header: "Gross Total",
            meta: {
              isNumeric: true
            }
        })
    ];

    const handleAddTransaction = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.post(
                `/api/assets/${asset.id}/transactions`,
                newTransaction,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast({
                title: 'Transaction added.',
                description: 'The transaction has been added successfully.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            setNewTransaction({
                date: '',
                transaction_type: '',
                quantity: '',
                price_per_unit: '',
                net_total: '',
                fees: '0.0',
                gross_total: '',
            });
        } catch (error) {
            toast({
                title: 'Error adding transaction.',
                description: 'There was an error adding the transaction.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }

        // empty the transactions array and fetch the transactions again
        setTransactions([]);
        fetchTransactions();
        getAssetDetails();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Asset Details</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                        <GridItem>
                            <Heading size="sm">Ticker</Heading>
                            {isEditing ? (
                                <Input
                                    name="ticker"
                                    value={editedAsset.ticker}
                                    onChange={handleAssetInputChange}
                                />
                            ) : (
                                <p>{asset.ticker}</p>
                            )}
                        </GridItem>
                        <GridItem>
                            <Heading size="sm">ISIN</Heading>
                            {isEditing ? (
                                <Input
                                    name="isin"
                                    value={editedAsset.isin}
                                    onChange={handleAssetInputChange}
                                />
                            ) : (
                                <p>{asset.isin}</p>
                            )}
                        </GridItem>
                        <GridItem>
                            <Heading size="sm">Asset type</Heading>
                            {isEditing ? (
                                <Input
                                    name="type"
                                    value={editedAsset.type}
                                    onChange={handleAssetInputChange}
                                />
                            ) : (
                                <p>{asset.type}</p>
                            )}
                        </GridItem>
                        <GridItem>
                            <Heading size="sm">Name</Heading>
                            {isEditing ? (
                                <Input
                                    name="name"
                                    value={editedAsset.name}
                                    onChange={handleAssetInputChange}
                                />
                            ) : (
                                <p>{asset.name}</p>
                            )}
                        </GridItem>
                        <GridItem>
                            <Heading size="sm">Broker</Heading>
                            {isEditing ? (
                                <Input
                                    name="broker"
                                    value={editedAsset.broker}
                                    onChange={handleAssetInputChange}
                                />
                            ) : (
                                <p>{asset.broker}</p>
                            )}
                        </GridItem>
                        <GridItem>
                            <Heading size="sm">Quantity</Heading>
                            {isEditing ? (
                                <Input
                                    name="quantity"
                                    value={editedAsset.quantity}
                                    onChange={handleAssetInputChange}
                                />
                            ) : (
                                <p>{asset.quantity}</p>
                            )}
                        </GridItem>
                        <GridItem>
                            <Heading size="sm">Total value</Heading>
                            <p>{assetPrice * asset.quantity}</p>
                        </GridItem>
                        <GridItem>
                            <Heading size="sm">Currency</Heading>
                            {isEditing ? (
                                <Input
                                    name="currency"
                                    value={editedAsset.currency}
                                    onChange={handleAssetInputChange}
                                />
                            ) : (
                                <p>{asset.currency}</p>
                            )}
                        </GridItem>
                    </Grid>

                    <Box mt={2} display="flex" justifyContent="flex-end">
                        {isEditing ? (
                            <>
                                <Button colorScheme="teal" mr={3} onClick={handleConfirmClick}>
                                    Confirm
                                </Button>
                                <Button variant="ghost" onClick={handleCancelClick}>
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <Button colorScheme="teal" mr={3} onClick={handleEditClick}>
                                <EditIcon />
                            </Button>
                        )}
                    </Box>
                    <Box mt={4}>
                        <Heading size="sm" mb={2}>Transactions</Heading>

                        <Heading size="xs" mb={2}>Add Transaction</Heading>
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>Date</Th>
                                    <Th>Type</Th>
                                    <Th>Quantity</Th>
                                    <Th>Price per Unit</Th>
                                    <Th>Fees</Th>
                                    <Th></Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                <Tr>
                                    <Td>
                                        <Input
                                            type="date"
                                            name="date"
                                            value={newTransaction.date}
                                            onChange={handleInputChange}
                                        />
                                    </Td>
                                    <Td>
                                        <Select
                                            name="transaction_type"
                                            value={newTransaction.transaction_type}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select type</option>
                                            <option value="BUY">BUY</option>
                                            <option value="SELL">SELL</option>
                                        </Select>
                                    </Td>
                                    <Td>
                                        <Input
                                            type="number"
                                            name="quantity"
                                            value={newTransaction.quantity}
                                            onChange={handleInputChange}
                                        />
                                    </Td>
                                    <Td>
                                        <Input
                                            type="number"
                                            name="price_per_unit"
                                            value={newTransaction.price_per_unit}
                                            onChange={handleInputChange}
                                        />
                                    </Td>
                                    <Td>
                                        <Input
                                            type="number"
                                            name="fees"
                                            value={newTransaction.fees}
                                            onChange={handleInputChange}
                                        />
                                    </Td>
                                    <Td>
                                        <Button colorScheme="teal" mr={3} onClick={handleAddTransaction}>
                                            <CheckIcon />
                                        </Button>
                                    </Td>
                                </Tr>
                            </Tbody>
                        </Table>

                        <Heading size="xs" mt={2} mb={2}>Transactions</Heading>
                        <AdvancedDataTable columns={columns} data={transactions} />
                    </Box>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" onClick={onClose}>Close</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AssetDetailsModal;